import { Component, HostListener, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from '../../../services/authentication/authentication.service';
import { RegisterAccountModel } from '../../../services/authentication/models/register-account-model';
import { EmailVerificationModel } from 'src/app/services/authentication/models/email-verification-model';
import { NgxSpinnerService } from 'ngx-spinner';
import { Ga4Service } from '../../../services/ga4/ga4.service';
import { v4 as uuidv4 } from 'uuid';
import { HyperFlowChatbotService } from 'src/app/services/hyperflow-chatbot/hyperflow-chatbot.service';
import { RedirectValidatorService } from 'src/app/services/shared/redirect-validator.service';
import { IRedirect } from 'src/app/shared/interfaces/redirect';
import { ActivatedRoute } from '@angular/router';
import { ValidateCustomCpf } from '../../shared/validators/type-validators';

@Component({
  selector: 'app-registration',
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.scss']
})
export class RegistrationComponent implements OnInit {
  registrationForm: UntypedFormGroup;
  termsPdfLink: string = environment.urlTermsPdf;
  registerSuccessfully: boolean = false;
  mobileScreen: boolean;
  screenWidth: number;
  token: string = '';
  registrationId: string;
  showPassword: boolean = false;
  completeRegister = false;
  initialResent = false;
  isMinLengthValid: boolean = false;
  isUppercaseValid: boolean = false;
  isLowercaseValid: boolean = false;
  isNumberValid: boolean = false;
  isSpecialCharValid: boolean = false;
  inviteToken: string = '';
  ignoreCpf: boolean | null = null;

  constructor(
    private fb: UntypedFormBuilder,
    private authenticationService: AuthenticationService,
    private toastrService: ToastrService,
    private spinner: NgxSpinnerService,
    private ga4Service: Ga4Service,
    private hyperflowChatbotService: HyperFlowChatbotService,
    private redirectValidatorService: RedirectValidatorService,
    private route: ActivatedRoute
  ) {
    this.inviteToken = this.route.snapshot.queryParamMap.get('inviteToken');
    this.getScreenSize();
  }

  redirect?: IRedirect;

  ngOnInit(): void {
    const redirect = this.redirectValidatorService.getValidRedirectParam();
    this.redirect = redirect;

    this.hyperflowChatbotService.initWithScriptToken(
      environment.desloggedAreaChatbotToken
    );
    this.setRegistrationId();
    this.initializeForm();
    this.getFormStorage();

    const emailFromRedirect = this.route.snapshot.queryParamMap.get('email');
    const completeRegisterParam = this.route.snapshot.queryParamMap.get('completeRegister');
    const ignoreCpfParam = this.route.snapshot.queryParamMap.get('ignoreCpf');

    if (ignoreCpfParam === 'true') {
      this.ignoreCpf = true;
    } else if (ignoreCpfParam === 'false') {
      this.ignoreCpf = false;
    }

    if (emailFromRedirect) {
      this.registrationForm.patchValue({ email: emailFromRedirect });
      if (completeRegisterParam === 'true') {
        this.completeRegister = true;
      } else {
        this.sendVerificationAndShow(emailFromRedirect);
      }
    }
  }

  initializeForm() {
    this.registrationForm = this.fb.group({
      email: [
        '',
        [
          Validators.required,
          Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')
        ]
      ],
      cpf: ['', [Validators.required, ValidateCustomCpf]],
      password: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/
          )
        ]
      ]
    });

    this.registrationForm.get('password')?.valueChanges.subscribe((value) => {
      this.validatePassword(value);
    });
  }

  setFormStorage(field: string) {
    const email = this.registrationForm.get('email')?.value?.toLowerCase();
    const password = this.registrationForm.get('password')?.value;
    this.registrationForm.controls['email'].setValue(email);
    this.registrationForm.controls['password'].setValue(password);
    localStorage.setItem(
      'registrationForm',
      JSON.stringify(this.registrationForm.value)
    );

    // this.emitFieldChange(field);
  }

  // emitFieldChange(field: string): void {
  //   const event = `${field}_preenchido`;
  //   this.ga4Service.emit(event, this.registrationId);
  // }

  getFormStorage() {
    this.registrationForm.patchValue(
      JSON.parse(localStorage.getItem('registrationForm'))
    );
  }

  onSubmit() {
    if (this.registrationForm.invalid) {
      this.registrationForm.markAllAsTouched();

      return;
    }
    this.registerAccount();
  }

  registerAccount() {
    this.spinner.show();
    const register = new RegisterAccountModel();
    register.email = this.registrationForm.get('email')?.value;
    register.password = this.registrationForm.get('password')?.value;
    register.cpf = this.registrationForm.get('cpf')?.value;

    this.authenticationService
      .registerAccount(register, this.inviteToken)
      .subscribe(
        () => {
          this.registerSuccessfully = true;
          this.ga4Service.emit('cadastro_iniciado');
          localStorage.removeItem('registrationForm');
        },
        (err) => {
          this.spinner.hide();
          if (err?.status === 403) {
            this.toastrService.warning('Este e-mail já possui um cadastro iniciado. Verifique sua caixa de entrada para ativar sua conta.');
            this.sendVerificationAndShow(this.registrationForm.get('email').value);
            return;
          }
          if (err?.status === 400 && err?.error?.message) {
            this.toastrService.error(err.error.message as string);
            return;
          }
          this.toastrService.error('Não foi possível concluir o cadastro. Tente novamente.');
        }
      );
  }

  private sendVerificationAndShow(email: string): void {
    this.registerSuccessfully = true;
    this.authenticationService.resendRegisterToken(email).subscribe({
      next: () => {
        this.toastrService.success('Código de verificação enviado para seu e-mail.');
        this.initialResent = true;
      },
      error: () => {
        this.toastrService.warning('Não foi possível enviar o código automaticamente. Use o botão "Envie novamente".');
      }
    });
  }

  resendEmail() {
    const validateRequest: EmailVerificationModel = {
      verificationToken: this.token,
      email: this.registrationForm.get('email')?.value
    };

    this.authenticationService.verificationEmail(validateRequest).subscribe(
      () => {
        this.toastrService.success(
          'E-mail enviado com sucesso. Verifique seu spam.'
        );
        this.registerSuccessfully = true;
        localStorage.removeItem('registrationForm');
      },
      () => {
        this.toastrService.error('Token inválido.', 'Erro');
      }
    );
  }

  pad(num: string, size: number, char: string): string {
    let s = num + '';
    while (s.length < size) s = char + s;
    return s;
  }

  @HostListener('window:resize')
  getScreenSize() {
    this.screenWidth = window.innerWidth;
    this.mobileScreen = this.screenWidth <= 576;
  }

  setRegistrationId(): void {
    this.registrationId = localStorage.getItem('registrationId');
    if (!this.registrationId) {
      this.registrationId = uuidv4();
      localStorage.setItem('registrationId', this.registrationId);
    }
  }

  onBack(): void {
    this.ga4Service.emit('voltar_acionado', this.registrationId);
  }

  validatePassword(password: string) {
    this.isMinLengthValid = password.length >= 8;
    this.isUppercaseValid = /[A-Z]/.test(password);
    this.isLowercaseValid = /[a-z]/.test(password);
    this.isNumberValid = /\d/.test(password);
    this.isSpecialCharValid = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  }

  onTokenValidated(): void {
    this.registerSuccessfully = false;
    this.completeRegister = true;
    this.ignoreCpf = true;
  }
}
